import { useSyncExternalStore } from 'react';

/**
 * Dev-only discovery registry: every mounted `ExperienceSlot` announces its
 * experiment key and variant list here (DEV builds only), and the experiments
 * devtool renders whatever is registered. No hardcoded experiment list — a
 * new slot anywhere in the app shows up in the devtool automatically.
 */
export interface RegisteredExperiment {
  experiment: string;
  variants: string[];
}

const registry = new Map<string, string[]>();
const listeners = new Set<() => void>();
/** Cached snapshot — useSyncExternalStore needs referential stability. */
let snapshot: RegisteredExperiment[] = [];

function rebuild(): void {
  snapshot = [...registry.entries()]
    .map(([experiment, variants]) => ({ experiment, variants }))
    .sort((a, b) => a.experiment.localeCompare(b.experiment));
  for (const listener of listeners) listener();
}

export function registerExperimentForDevtool(experiment: string, variants: string[]): void {
  const existing = registry.get(experiment);
  if (existing && existing.join(' ') === variants.join(' ')) return;
  registry.set(experiment, variants);
  rebuild();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useRegisteredExperiments(): RegisteredExperiment[] {
  return useSyncExternalStore(subscribe, () => snapshot);
}

/** Test-only escape hatch. */
export function __resetDevtoolRegistryForTests(): void {
  registry.clear();
  rebuild();
}
